
// client/src/services/scheduledPostsApi.js
// API service for scheduled social media posts
// Handles fetching, creating, and cancelling scheduled posts

/**
 * Fetch upcoming scheduled posts for a tenant
 * 
 * @param {number} tenantId - Tenant ID
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of posts to return
 * @param {number} options.offset - Number of posts to skip (for pagination)
 * @returns {Promise<Object>} Response with posts array and pagination metadata
 * 
 * Response format:
 * {
 *   success: true,
 *   posts: [...],
 *   pagination: { total, limit, offset, hasMore }
 * }
 */
export async function fetchUpcomingScheduledPosts(tenantId, options = {}) {
  try {
    const { limit = 50, offset = 0 } = options;

    // Validate tenantId
    if (!tenantId || isNaN(tenantId)) {
      throw new Error("Valid tenantId is required");
    }

    // Build query string
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });

    // const response = await fetch(
    //   `/api/schedule/upcoming/${tenantId}?${params.toString()}`,
    //   {
    //     method: "GET",
    //     headers: {
    //       "Content-Type": "application/json",
    //     },
    //   }
    // );

    const response = await fetch(
      `/api/schedule/upcoming/${tenantId}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",  // ADD THIS LINE
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error ${response.status}`
      );
    }

    const data = await response.json();

    // Validate response structure
    if (!data.success) {
      throw new Error(data.error || "Failed to fetch scheduled posts");
    }

    // Return the full response with posts and pagination
    return {
      posts: Array.isArray(data.posts) ? data.posts : [],
      pagination: data.pagination || {
        total: 0,
        limit,
        offset,
        hasMore: false,
      },
    };
  } catch (error) {
    console.error("Error fetching upcoming scheduled posts:", error);
    throw error;
  }
}

/**
 * Cancel a scheduled post
 * Only works for posts in 'queued' or 'posting' status
 * 
 * @param {number} postId - Post ID to cancel
 * @param {number} tenantId - Tenant ID (for authorization)
 * @returns {Promise<Object>} Response with success status
 * 
 * Response format:
 * {
 *   success: true,
 *   message: "Post cancelled successfully",
 *   postId: number
 * }
 */
export async function cancelScheduledPost(postId, tenantId) {
  try {
    // Validate inputs
    if (!postId || isNaN(postId)) {
      throw new Error("Valid postId is required");
    }

    if (!tenantId || isNaN(tenantId)) {
      throw new Error("Valid tenantId is required");
    }

    // const response = await fetch(`/api/schedule/${postId}`, {
    //   method: "DELETE",
    //   headers: {
    //     "Content-Type": "application/json",
    //   },
    //   body: JSON.stringify({ tenantId }),
    // });

    const response = await fetch(`/api/schedule/${postId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",  // ADD THIS LINE
      body: JSON.stringify({ tenantId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error ${response.status}`
      );
    }

    const data = await response.json();

    // Validate response
    if (!data.success) {
      throw new Error(data.error || "Failed to cancel post");
    }

    return data;
  } catch (error) {
    console.error("Error cancelling scheduled post:", error);
    throw error;
  }
}

/**
 * Schedule a new social media post
 * 
 * @param {Object} postData - Post data
 * @param {number} postData.tenantId - Tenant ID
 * @param {number} [postData.eventId] - Optional event ID
 * @param {number} [postData.eventItemId] - Optional event item ID
 * @param {string[]} postData.platforms - Array of platform names
 * @param {string} postData.content - Post content/caption
 * @param {string[]} [postData.mediaUrls] - Array of media URLs
 * @param {string} [postData.scheduledAt] - Scheduled datetime (ISO format or MySQL format)
 * @param {string} [postData.scheduledAtIso] - Scheduled datetime (ISO format)
 * @param {string} [postData.scheduledAtLocal] - Scheduled datetime (MySQL format)
 * @returns {Promise<Object>} Response with scheduled post details
 * 
 * Response format:
 * {
 *   success: true,
 *   id: number,
 *   scheduledOn: string,
 *   platforms: string[],
 *   message: string
 * }
 */
export async function schedulePost(postData) {
  try {
    // Validate required fields
    if (!postData.tenantId || isNaN(postData.tenantId)) {
      throw new Error("Valid tenantId is required");
    }

    if (!Array.isArray(postData.platforms) || postData.platforms.length === 0) {
      throw new Error("At least one platform is required");
    }

    if (!postData.content?.trim() && (!postData.mediaUrls || postData.mediaUrls.length === 0)) {
      throw new Error("Either content or media is required");
    }

    const response = await fetch("/api/schedule/schedule", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        tenantId: postData.tenantId,
        eventId: postData.eventId || null,
        eventItemId: postData.eventItemId || null,
        platforms: postData.platforms,
        content: postData.content || "",
        mediaUrls: postData.mediaUrls || [],
        scheduledAt: postData.scheduledAt || null,
        scheduledAtIso: postData.scheduledAtIso || null,
        scheduledAtLocal: postData.scheduledAtLocal || null,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error ${response.status}`
      );
    }

    const data = await response.json();

    // Validate response
    if (!data.success) {
      throw new Error(data.error || "Failed to schedule post");
    }

    return data;
  } catch (error) {
    console.error("Error scheduling post:", error);
    throw error;
  }
}

/**
 * Get system health check for scheduled posts
 * Returns aggregated statistics about posts and jobs
 * 
 * @returns {Promise<Object>} Health check data
 * 
 * Response format:
 * {
 *   success: true,
 *   timestamp: string,
 *   posts: { due, posting, posted, failed, cancelled, lastChange },
 *   jobs: { queued, posting, posted, failed, lastChange }
 * }
 */
export async function getScheduleHealth() {
  try {
    const response = await fetch("/api/schedule/health", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.error || `HTTP error ${response.status}`
      );
    }

    const data = await response.json();

    // Validate response
    if (!data.success) {
      throw new Error(data.error || "Failed to get health check");
    }

    return data;
  } catch (error) {
    console.error("Error fetching schedule health:", error);
    throw error;
  }
}

/**
 * Helper function to format date for display
 * Can be used by components to consistently format dates
 * 
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date string
 */
export function formatScheduledDate(date) {
  if (!date) return "Not scheduled";
  
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return String(date);
    }
    
    // Format: "Jan 8, 2026, 10:30 AM"
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(date);
  }
}

/**
 * Helper function to get status badge color class
 * 
 * @param {string} status - Post status
 * @returns {string} Bootstrap color class
 */
export function getStatusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  
  switch (s) {
    case "queued":
      return "bg-primary";
    case "posting":
      return "bg-info";
    case "posted":
      return "bg-success";
    case "failed":
      return "bg-danger";
    case "cancelled":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
}

/**
 * Helper function to check if a post can be cancelled
 * 
 * @param {Object} post - Post object
 * @returns {boolean} True if post can be cancelled
 */
export function canCancelPost(post) {
  const status = String(post?.status || "").toLowerCase();
  return status === "queued" || status === "posting";
}